import { apiConnection } from "@/services/api-connection";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import { stringManager } from "@/services/utilities/string-manager";
import router from "@/router";
import { messengerMessageManager } from "@/services/messenger-message/messenger-message-manager";
import { accessManager } from "@/services/security/access-manager";
import { accessConstante } from "@/constantes/access";
import { registrationConstante } from "@/constantes/registration";
import { formManager } from "@/services/form/form-manager";
import { clipboardManager } from "@/services/clipboard/clipboard-manager";
import { registrationDocumentStatusConstantes } from "@/constantes/registrationDocumentStatus";

const URL_REGISTRATION = "/app/registration/";
const URL_TRAINING_SHEET = "/app/registration/download-training-sheet/";
const URL_VERSION = "/app/admin/registration-data-version/";
const URL_DEFAUTL_TEMPLATE_DOWNLOAD = "/app/default-";
const URL_DOCUMENT = "/app/registration/registration-document/save/";
const URL_QUOTE = "/app/devis/";
const URL_COMMITMENTS = "/app/certificat-inscription/";

class RegistrationManager {
  REGISTRATION_DATA_VERSION_CERFA_PARAM = 1;
  REGISTRATION_DATA_VERSION_FIELD_CONFIG = 2;
  REGISTRATION_DATA_VERSION_STEP = 3;
  GROUP_STATUS_PENDING = 1;
  GROUP_STATUS_TO_VALIDATE = 2;
  GROUP_STATUS_FINALIZED = 3;

  form(groupCryptedId, registrationCryptedId, type = null) {
    return apiConnection
      .get(
        URL_REGISTRATION +
          (type ? type + "/" : "") +
          groupCryptedId +
          "/form" +
          (registrationCryptedId ? "/" + registrationCryptedId : ""),
      )
      .then((response) => response);
  }

  async search(alias, studeaManager = false, params = {}) {
    return await apiConnection.get("/app/" + (studeaManager ? "studea-manager/" : "") + alias + "/search-form", params);
  }

  getVersion(id) {
    return apiConnection.get(URL_VERSION + "get/" + id).then((response) => response);
  }

  list(params, alias, studeaManager = false) {
    return apiConnection
      .get("/app/" + (studeaManager ? "studea-manager/" : "") + alias + "/list", params)
      .then((response) => response);
  }

  async export(alias, params) {
    const res = await apiConnection.get(URL_REGISTRATION + alias, params);
    messengerMessageManager.processMessage(res);
  }

  getRegistration(id) {
    return apiConnection.get(URL_REGISTRATION + "get/" + id).then((response) => response);
  }

  downloadTrainingSheet(id) {
    return apiConnection.get(URL_TRAINING_SHEET + id).then((response) => {
      messengerMessageManager.processMessage(response);
    });
  }

  menu() {
    return apiConnection.get(URL_REGISTRATION + "menu").then((response) => response);
  }

  statistics() {
    return apiConnection.get(URL_REGISTRATION + "statistic").then((response) => response);
  }

  version(params, id) {
    let url = URL_VERSION + "post";
    if (id) {
      url = URL_VERSION + "put" + "/" + id;
    }
    return (id ? apiConnection.put(url, params) : apiConnection.post(url, params)).then((res) => {
      if (res && !res.hasError) {
        notificationManager.showNotification(
          "success",
          i18n.global.tc("global." + (id ? "edit" : "add") + ".success", 2, {
            name: i18n.global.tc("admin.registrationDataVersion.title", 1),
          }),
        );
      } else {
        notificationManager.showNotification("error", i18n.global.t("signed_error"));
      }
    });
  }

  active(alias, id) {
    let tradPrefix = alias;
    if (alias.includes("/")) {
      tradPrefix = alias.split("/").at(-1);
    }
    tradPrefix = stringManager.toCamelCase(tradPrefix);
    return apiConnection.put("/app/admin/" + alias + "/active/" + id).then((res) => {
      if (res && res.success) {
        notificationManager.showNotification(
          "success",
          i18n.global.tc("global.edit.success", 2, {
            name: i18n.global.tc("admin." + tradPrefix + ".title", 1),
          }),
        );
      } else {
        notificationManager.showNotification("error", i18n.global.t("signed_error"));
      }
    });
  }

  save(params, groupCryptedId, tabId, registrationCryptedId, validateStep = false, type = null) {
    let url =
      URL_REGISTRATION +
      (type ? type + "/" : "") +
      groupCryptedId +
      (registrationCryptedId ? "/put/" : "/post/") +
      tabId +
      (registrationCryptedId ? "/" + registrationCryptedId : "") +
      (validateStep ? "?validateStep=1" : "");
    return (registrationCryptedId ? apiConnection.put(url, params) : apiConnection.post(url, params)).then(
      (res) => res,
    );
  }

  async ypareoSync(registrationCryptedId) {
    return await apiConnection.put(URL_REGISTRATION + registrationCryptedId + "/ypareo-sync");
  }

  async ypareoSyncLog(registrationCryptedId) {
    return await apiConnection.get(URL_REGISTRATION + registrationCryptedId + "/ypareo-sync-log");
  }

  put(alias, params = {}, id = null) {
    const url = "/app/studea-manager/" + encodeURI(alias) + "/post" + (id ? "/" + id : "");
    return apiConnection.put(url, params);
  }

  putOneField(groupCryptedId, registrationField, params, registrationCryptedId = null) {
    return apiConnection
      .put(
        "/app/registration/" +
          groupCryptedId +
          "/put-one-field/" +
          registrationField +
          (registrationCryptedId ? "/" + registrationCryptedId : ""),
        params,
      )
      .then((res) => res);
  }

  getRegistrationCreateForm(params = {}) {
    return apiConnection.get("/app/registration/apprentice/form", params).then((res) => res);
  }

  postRegistrationCreateForm(group, result, currentRouter = router) {
    apiConnection.post("/app/registration/apprentice/" + group + "/post", result).then((res) => {
      notificationManager.showNotification(
        "success",
        i18n.global.tc("registration.creation.success", {
          firstName: result.data.firstName,
          lastName: result.data.lastName,
        }),
      );
      currentRouter.push({
        name: "TrainingPreRegistration",
        params: {
          group: group,
          tab: 0,
          registration: res.responses[0]?.object?.cryptedId,
        },
      });
    });
  }

  getRegistrationQuoteFinancialData(cryptedId) {
    return apiConnection.get("/app/estimate/billing-date/" + cryptedId).then((res) => res);
  }

  clickButton(url, data) {
    return apiConnection.fetchApi("put", url, data).then((res) => res);
  }

  getEstimate(alias, params = {}, id = null) {
    const url = "/app/estimate/" + encodeURI(alias) + (id ? "/" + id : "");
    return apiConnection.get(url, params);
  }

  getSpecificCalendarForm(trainingCourseId, registrationGroupId, tab, registrationCryptedId) {
    return apiConnection
      .get(
        "/app/registration-group/" +
          trainingCourseId +
          "/form/edit/" +
          registrationGroupId +
          "/" +
          tab +
          "/" +
          registrationCryptedId,
      )
      .then((res) => res);
  }

  putRegistrationContractDates(trainingCourseId, registrationGroupId, tab, registrationCryptedId, data) {
    return apiConnection
      .put(
        "/app/registration-group/" +
          trainingCourseId +
          "/form/put/" +
          registrationGroupId +
          "/" +
          tab +
          "/" +
          registrationCryptedId,
        data,
      )
      .then((res) => res);
  }

  updateRegistrationGroupData(registration, result, calendarFile) {
    registration.registrationGroup.hourlyTrainingVolume = result.hourlyTrainingVolume;
    registration.registrationGroup.hourlyRemoteTeachingVolume = result.hourlyRemoteTeachingVolume;
    registration.registrationGroup.startDate = result.startDate;
    registration.registrationGroup.endDate = result.endDate;
    registration.registrationGroup.calendar = calendarFile;
  }

  async getEditCostForm(registrationCryptedId, isPro = false, year = null) {
    return await apiConnection.get(
      "/app/estimate/open-modal" + (isPro ? "-pro" : "") + "/" + registrationCryptedId + (year ? "/" + year : ""),
    );
  }

  async updateCost(registrationCryptedId, data, year) {
    if (!year || (data && !Object.keys(data).length)) {
      return null;
    }
    return await apiConnection.post("/app/estimate/update-modal-pro/" + registrationCryptedId + "/" + year, data);
  }

  async postCost(registrationCryptedId, data, year) {
    return await apiConnection.post(
      "/app/estimate/post-modal" + (year ? "-pro" : "") + "/" + registrationCryptedId + (year ? "/" + year : ""),
      data,
    );
  }

  async getHistoric(registrationCryptedId) {
    return await apiConnection.get("/app/estimate/historic/" + registrationCryptedId);
  }

  setAlertActionIcon(currentAccessManager = accessManager) {
    const isModuleAvailable = currentAccessManager.isAvailableForUser(
      null,
      accessConstante.STUDEA_MANAGER_REGISTRATION_ALERT_EDIT,
      accessConstante.EDIT,
    );

    if (isModuleAvailable) return "pen";
    return "eye";
  }

  setAlertActionTootltip(currentAccessManager = accessManager) {
    const isModuleAvailable = currentAccessManager.isAvailableForUser(
      null,
      accessConstante.STUDEA_MANAGER_REGISTRATION_ALERT_EDIT,
      accessConstante.EDIT,
    );

    if (isModuleAvailable)
      return i18n.global.tc("global.edit.title", 2, {
        name: i18n.global.tc("registration.alert.title", 1).toLowerCase(),
      });
    return i18n.global.t("Watch");
  }

  processTabResult(tabs) {
    const salaryTab = Object.values(tabs).find(
      (tab) => tab.code === registrationConstante.REGISTRATION_SALARY_TAB_CODE,
    );
    let result = {};
    salaryTab.blocks.forEach((block) => {
      const blockResult = formManager.processForm(block.models);
      result = { ...result, ...blockResult };
    });
    return result;
  }

  async putSalaryTab(registrationCryptedId, groupCryptedId, tab, data) {
    return await apiConnection.put(
      "/app/registration/salary/" + groupCryptedId + "/reload/" + tab + "/" + registrationCryptedId,
      data,
    );
  }

  async downloadDefaultTemplate(type, registrationId) {
    return await apiConnection.get(URL_DEFAUTL_TEMPLATE_DOWNLOAD + type + "/download/" + registrationId);
  }

  async postDocument(tab, registrationId, data) {
    return await apiConnection.put(URL_DOCUMENT + tab + "/" + registrationId, data);
  }

  async copyToClipboard(id) {
    let url = window.location.origin + "/registration/" + id + "/create";
    clipboardManager.copy(url).then(() => {
      notificationManager.showNotification("success", i18n.global.t("registration.link_copied_success"));
    });
  }

  getQuoteAmountColumnTitle(type) {
    switch (type) {
      case registrationConstante.CONTRACT_APPRENTICESHIP_APPRENTISSAGE:
        return [
          { title: "" },
          {
            title: i18n.global.t("registration.quote.table.columns.trainingCourseCost"),
          },
          { title: i18n.global.t("registration.quote.table.columns.opcoo") },
          {
            title: i18n.global.t("registration.quote.table.columns.mandatory_participation"),
            tooltip: i18n.global.t("registration.quote.table.columns.mandatory_participation_tooltip"),
          },
          {
            title: i18n.global.t("registration.quote.table.columns.remaining_cost"),
            tooltip: i18n.global.t("registration.quote.table.columns.remaining_cost_tooltip"),
          },
        ];
      case registrationConstante.CONTRACT_PRO_CFA:
        return [
          { title: "" },
          { title: i18n.global.t("registration.hourlyVolume") },
          { title: i18n.global.t("registration.hourlyCost") },
          { title: i18n.global.t("registration.prestationCost") },
        ];
    }
  }

  async downloadQuote(registrationId, contractType) {
    const endpoint = contractType === 2 ? "download-pro/" : "download/";
    return await apiConnection.get(URL_QUOTE + endpoint + registrationId);
  }

  async handleCancelRegistration(registrationId, registrationType) {
    return await apiConnection.put(URL_REGISTRATION + registrationType + "/" + registrationId);
  }

  async getRegistrationContact(registrationCryptedId) {
    return await apiConnection.get("/app/registration/" + registrationCryptedId + "/contact/get");
  }

  async deleteRegistrationContact(contactId, registrationCryptedId) {
    return await apiConnection.delete("/app/registration/" + registrationCryptedId + "/contact/remove/" + contactId);
  }

  async getCompanyContact(registrationCryptedId) {
    return await apiConnection.get("/app/registration/" + registrationCryptedId + "/contact/step-company");
  }

  async downloadCommitmentsCertificate(registrationCryptedId) {
    return await apiConnection.get(URL_COMMITMENTS + "download/" + registrationCryptedId);
  }

  getRegistrationDocumentStatusText(status, actorType) {
    // Libellés volontairement croisés CFA/Entreprise : un même statut (SENT/TO_SEND)
    // se lit différemment selon le point de vue de l'acteur. Ne pas "corriger" l'inversion.
    switch (status) {
      case registrationDocumentStatusConstantes.CORRECTION_REQUESTED:
        return actorType === registrationConstante.REGISTRATION_ACTOR_CFA
          ? i18n.global.t("registration.document.status.correction_requested")
          : i18n.global.t("registration.document.status.to_be_modified");
      case registrationDocumentStatusConstantes.VERIFIED:
        return actorType === registrationConstante.REGISTRATION_ACTOR_CFA
          ? i18n.global.t("registration.document.status.verified")
          : i18n.global.t("registration.document.status.validated");
      case registrationDocumentStatusConstantes.SENT:
        return actorType === registrationConstante.REGISTRATION_ACTOR_CFA
          ? i18n.global.t("registration.document.status.to_send")
          : i18n.global.t("registration.document.status.sent");
      case registrationDocumentStatusConstantes.DEPOSITED:
        return actorType === registrationConstante.REGISTRATION_ACTOR_CFA
          ? i18n.global.t("registration.document.status.to_verify")
          : i18n.global.t("registration.document.status.deposited");
      case registrationDocumentStatusConstantes.TO_SEND:
      default:
        return actorType === registrationConstante.REGISTRATION_ACTOR_CFA
          ? i18n.global.t("registration.document.status.sent")
          : i18n.global.t("registration.document.status.to_send");
    }
  }

  getRegistrationDocumentStatusIcon(status, actorType) {
    switch (status) {
      case registrationDocumentStatusConstantes.CORRECTION_REQUESTED:
        return actorType === registrationConstante.REGISTRATION_ACTOR_CFA ? "hourglass-half" : "pen-ruler";
      case registrationDocumentStatusConstantes.VERIFIED:
        return "check";
      case registrationDocumentStatusConstantes.SENT:
        return actorType === registrationConstante.REGISTRATION_ACTOR_CFA ? "arrow-up" : "circle-check";
      case registrationDocumentStatusConstantes.DEPOSITED:
        return actorType === registrationConstante.REGISTRATION_ACTOR_CFA ? "clipboard-check" : "file-lines";
      case registrationDocumentStatusConstantes.TO_SEND:
      default:
        return actorType === registrationConstante.REGISTRATION_ACTOR_CFA ? "clipboard-check" : "paper-plane";
    }
  }

  async handleAlert(alert, alertType, shouldConfirm = true) {
    const alertConfirmation = await notificationManager.showAlert(
      alertType,
      alert.title ?? "",
      alert.content ?? "",
      true,
      shouldConfirm,
      alert.labelConfirm ?? i18n.global.t("confirm"),
      alert.labelCancel ?? i18n.global.t("cancel"),
      "#000",
      "",
      !!alert.content,
    );
    if (!shouldConfirm) return true;
    return alertConfirmation.isConfirmed;
  }

  hasPopup(popup) {
    return popup && Object.keys(popup).length > 0 && popup.enabled;
  }
}

export const registrationManager = new RegistrationManager();
